import React, { useState } from 'react';
import { 
  DeliveryPricingManagementProps, 
  DeliveryTier, 
  DeliveryPricingSettings,
  createEmptyDeliveryTier 
} from '../../../types';
import { Button } from '../../atoms';
import DeliveryTierForm from './DeliveryTierForm';
import DeliveryTierCard from './DeliveryTierCard';
import DeliverySettingsForm from './DeliverySettingsForm';
import './DeliveryPricingManagement.css';

const DeliveryPricingManagement: React.FC<DeliveryPricingManagementProps> = ({
  tiers = [],
  settings,
  loading = false,
  onCreateTier,
  onUpdateTier,
  onDeleteTier,
  onUpdateSettings
}) => {
  const [showTierForm, setShowTierForm] = useState(false);
  const [editingTier, setEditingTier] = useState<DeliveryTier | null>(null);
  const [showSettingsForm, setShowSettingsForm] = useState(false);

  const handleCreateTier = () => {
    setEditingTier(null);
    setShowTierForm(true);
  };

  const handleEditTier = (tier: DeliveryTier) => {
    setEditingTier(tier);
    setShowTierForm(true);
  };

  const handleTierSubmit = (tierData: Partial<DeliveryTier>) => {
    if (editingTier) {
      onUpdateTier?.(editingTier.id, tierData);
    } else {
      onCreateTier?.(tierData);
    }
    setShowTierForm(false);
    setEditingTier(null);
  };

  const handleTierCancel = () => {
    setShowTierForm(false);
    setEditingTier(null);
  };

  const handleDeleteTier = (tier: DeliveryTier) => {
    if (window.confirm(`Are you sure you want to delete the "${tier.name}" tier?`)) {
      onDeleteTier?.(tier.id);
    }
  };

  const handleSettingsSubmit = (settingsData: Partial<DeliveryPricingSettings>) => {
    onUpdateSettings?.(settingsData);
    setShowSettingsForm(false);
  };

  const handleSettingsCancel = () => {
    setShowSettingsForm(false);
  };

  return (
    <div className="delivery-pricing-management">
      <div className="delivery-pricing-header">
        <h3>Delivery Pricing Management</h3>
        <p>Manage delivery pricing tiers and settings for Dublin locations</p>
      </div>

      {/* Settings Section */}
      <div className="delivery-settings-section">
        <div className="section-header">
          <h4>Delivery Settings</h4>
          <Button 
            variant="secondary" 
            size="small"
            onClick={() => setShowSettingsForm(true)}
          >
            Edit Settings
          </Button>
        </div>
        
        {settings && (
          <div className="settings-summary">
            <div className="setting-item">
              <span className="setting-label">Default Delivery Price:</span>
              <span className="setting-value">€{settings.defaultDeliveryPrice.toFixed(2)}</span>
            </div>
            <div className="setting-item">
              <span className="setting-label">Free Delivery Threshold:</span>
              <span className="setting-value">€{settings.freeDeliveryThreshold.toFixed(2)}</span>
            </div>
            <div className="setting-item">
              <span className="setting-label">Status:</span>
              <span className={`setting-value status ${settings.isActive ? 'active' : 'inactive'}`}>
                {settings.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tiers Section */}
      <div className="delivery-tiers-section">
        <div className="section-header">
          <h4>Delivery Tiers ({tiers.length})</h4>
          <Button 
            variant="primary" 
            size="small"
            onClick={handleCreateTier}
            disabled={loading}
          >
            Create New Tier
          </Button>
        </div>

        {tiers.length === 0 ? (
          <div className="empty-state">
            <p>No delivery tiers created yet.</p>
            <p>Create your first tier to start managing delivery pricing by location.</p>
          </div>
        ) : (
          <div className="tiers-grid">
            {tiers.map(tier => (
              <DeliveryTierCard
                key={tier.id}
                tier={tier}
                onEdit={handleEditTier}
                onDelete={handleDeleteTier}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tier Form Modal */}
      {showTierForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <DeliveryTierForm
              tier={editingTier || undefined}
              onSubmit={handleTierSubmit}
              onCancel={handleTierCancel}
              loading={loading}
            />
          </div>
        </div>
      )}

      {/* Settings Form Modal */}
      {showSettingsForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <DeliverySettingsForm
              settings={settings}
              onSubmit={handleSettingsSubmit}
              onCancel={handleSettingsCancel}
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryPricingManagement;
